import * as React from 'react'
import Helmet from 'react-helmet'

import { Product } from './catalog/ProductPage/GetProduct.query'
import defaultAttributes from './catalog/ProductPage/StickyCta/defaultAttributes'
import flattenCombinedAttributes from './catalog/ProductPage/StickyCta/flattenCombinedAttributes'
import { useRootCategoryId } from './catalog/useRootCategory'
import { ProductStockStatus } from './graphql/schema.generated'
import { getBreadcrumbs } from './navigation/ProductBreadcrumbs'
import { getPrices } from './presentation/PriceBox'
import makeMediaUrl, { MediaType } from './utils/makeMediaUrl'

const ProductBlueConicMeta = (product: Product) => {
    const {
        id,
        mediaGalleryEntries,
        name,
        description,
        stockStatus,
        categories,
        baseColorValues,
    } = product

    const images = mediaGalleryEntries
        .filter((image) => image.file && !image.disabled)
        .sort((a, b) => a.position - b.position)
        .map(({ file }) => makeMediaUrl(file, MediaType.Product))

    const url = `${window.location.origin}${window.location.pathname}`
    const prices = getPrices(product)
    const rootCategoryId = useRootCategoryId()
    const flattenedOptions = flattenCombinedAttributes(product)
    const defaultOptions = defaultAttributes(product)
    const options =
        (flattenedOptions ?? defaultOptions)?.map((o) => ({
            key: o.value,
            label: o.label,
            isDisabled: o.disabled,
        })) ?? []
    // Regex to translate jeans sizes to correct format
    const jeansRegex = /^W(\d+) L(\d+)([a-zA-Z\s]+)?$/i
    // Regex to translate gear sizes to correct format
    const gearRegex = /^(\d+)([a-zA-Z\s]+)?$/i
    const sizes = options
        ?.map((option) =>
            option.label.replace(jeansRegex, '$1-$2').replace(gearRegex, '$1'),
        )
        .join(',')

    if (!rootCategoryId) {
        return null
    }

    const productCategories = getBreadcrumbs(categories, rootCategoryId)
    const category = productCategories[0].label || ''

    return (
        <Helmet>
            <meta property="bc:magentoid" content={id.toString()} />
            <meta property="bc:productname" content={name} />
            <meta property="bc:producttype" content={category} />
            <meta
                property="bc:retailprice"
                content={
                    prices && prices.regularPrice
                        ? prices.regularPrice.value.toString()
                        : ''
                }
            />
            <meta
                property="bc:specialprice"
                content={
                    prices && prices.specialPrice
                        ? prices.specialPrice.value.toString()
                        : ''
                }
            />
            <meta
                property="bc:availability"
                content={
                    stockStatus === ProductStockStatus.IN_STOCK
                        ? 'in stock'
                        : 'out of stock'
                }
            />
            <meta property="bc:qty" content="1" />
            <meta property="bc:size" content={sizes} />
            <meta
                property="bc:colorcode"
                content={baseColorValues?.[0]?.label || ''}
            />
            <meta
                property="bc:colorbasename"
                content={baseColorValues?.[0]?.label || ''}
            />
            <meta property="bc:url" content={url} />
            <meta property="bc:image1url" content={images[0] || ''} />
            <meta property="bc:image2url" content={images[1] || ''} />
            <meta property="bc:description" content={description?.html || ''} />
        </Helmet>
    )
}

export default ProductBlueConicMeta
